import { useMutation, useQueryClient } from "@tanstack/react-query";

import { qk } from "../../lib/queryKeys";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useCompanyDemoFlag } from "../companies/demoFlags";
import api from "../../utils/api";
import type {
  CentinelaConfigInput,
  CentinelaAlertPatchInput,
  CentinelaAlertPatchResponse,
  CentinelaNumeroInput,
  CentinelaTestNotificationInput,
  CentinelaTestNotificationResponse,
  CentinelaUnitCustodyInput,
  CentinelaGeofenceSuggestionsStart,
  CentinelaTestCallInput,
  CentinelaTripCustodyInput,
  CentinelaUnitCallInput,
  CentinelaUnitCallResponse,
  CentinelaUnitConfigInput,
  CentinelaUserNotificationsInput,
  CentinelaUsersResponse,
  CentinelaVozInput,
  CentinelaVozResponse,
} from "./types";

const demoParams = (isDemo: boolean) => (isDemo ? { demo: true } : undefined);

export const CENTINELA_USER_NOTIFICATIONS_MUTATION = ["centinela", "user-notifications"];

export function useUpdateCentinelaConfig() {
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const isDemo = useCompanyDemoFlag("centinela_demo");

  return useMutation({
    mutationFn: (input: CentinelaConfigInput): Promise<unknown> =>
      api
        .put("/centinela/config", input, { params: demoParams(isDemo) })
        .then((res) => res.data?.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.centinela.config(companyId, isDemo),
      });
    },
  });
}

export function useUpdateCentinelaTripCustody() {
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const isDemo = useCompanyDemoFlag("centinela_demo");

  return useMutation({
    mutationFn: ({
      tripId,
      input,
    }: {
      tripId: string;
      input: CentinelaTripCustodyInput;
    }): Promise<unknown> =>
      api
        .put(`/centinela/trips/${tripId}/custody`, input, { params: demoParams(isDemo) })
        .then((res) => res.data?.data),
    onSuccess: (_data, { tripId }) => {
      queryClient.invalidateQueries({
        queryKey: qk.centinela.tripCustody(companyId, tripId),
      });
    },
  });
}

export function useUpdateCentinelaUnitConfig() {
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const isDemo = useCompanyDemoFlag("centinela_demo");

  return useMutation({
    mutationFn: ({
      deviceId,
      input,
    }: {
      deviceId: string;
      input: CentinelaUnitConfigInput;
    }): Promise<unknown> =>
      api
        .put(`/centinela/config/units/${deviceId}`, input, {
          params: demoParams(isDemo),
        })
        .then((res) => res.data?.data),
    onSuccess: (_data, { deviceId }) => {
      queryClient.invalidateQueries({
        queryKey: qk.centinela.unitConfig(companyId, deviceId, isDemo),
      });
      queryClient.invalidateQueries({
        queryKey: qk.centinela.units(companyId, isDemo),
        refetchType: "none",
      });
    },
  });
}

export function useRequestCentinelaGeofenceSuggestions() {
  const isDemo = useCompanyDemoFlag("centinela_demo");

  return useMutation({
    mutationFn: (routeId: string): Promise<CentinelaGeofenceSuggestionsStart> =>
      api
        .post(`/centinela/routes/${routeId}/geofence-suggestions`, undefined, {
          params: demoParams(isDemo),
        })
        .then((res) => res.data?.data),
  });
}

/**
 * Marca una evidencia como inservible, o lo deshace.
 *
 * El sistema ya sospecha solo —una foto de pocos kilobytes casi siempre es la
 * pantalla de arranque de la cámara de cabina— pero la decisión es de una
 * persona: una noche cerrada o una lente empañada también comprimen poco, y
 * descartar en automático es la forma más rápida de perder la única foto de
 * algo que sí importaba.
 *
 * NO borra el archivo del bucket. Con `descartar: false` vuelve.
 */
export function useDescartarMedia(deviceId: string | null) {
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);

  return useMutation({
    mutationFn: ({
      object,
      descartar = true,
    }: {
      object: string;
      descartar?: boolean;
    }): Promise<unknown> =>
      api
        .post(`/centinela/units/${deviceId}/media/descartar`, { object, descartar })
        .then((res) => res.data?.data),
    onSuccess: () => {
      // Se invalida el día entero y no solo la galería: el contador de
      // evidencia de cada hecho también cambia.
      queryClient.invalidateQueries({ queryKey: ["centinela", "unit-conduccion", companyId] });
      queryClient.invalidateQueries({ queryKey: ["centinela", "unit-media", companyId] });
    },
  });
}

/**
 * Cierra un caso a mano.
 *
 * Centinela cierra cuando alguien le confirma por teléfono que todo está bien.
 * Si la unidad no tiene teléfono de operador ni supervisor con avisos, ese
 * camino no existe: el caso se queda abierto y la unidad escala a riesgo hasta
 * que vuelva a reportar. Esto es la salida.
 *
 * La nota es obligatoria y queda guardada junto con QUIÉN cerró. No es
 * burocracia: si después resulta que el caso sí requería atención, la pregunta
 * va a ser quién lo dio por bueno y con qué argumento.
 */
export function useCerrarCaso() {
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);

  return useMutation({
    mutationFn: ({
      caseId,
      resolution,
    }: {
      caseId: string;
      resolution: string;
    }): Promise<unknown> =>
      api
        .post(`/centinela/cases/${caseId}/cerrar`, { resolution })
        .then((res) => res.data?.data),
    onSuccess: () => {
      // El caso cambia de estado y con él la unidad: su semáforo deja de
      // escalar. Se invalida todo lo que lo refleja.
      queryClient.invalidateQueries({ queryKey: ["centinela", "cases", companyId] });
      queryClient.invalidateQueries({ queryKey: ["centinela", "units", companyId] });
      queryClient.invalidateQueries({ queryKey: ["centinela", "unit-events", companyId] });
    },
  });
}

export function useCentinelaTestCall() {
  return useMutation({
    mutationFn: (input: CentinelaTestCallInput): Promise<unknown> =>
      api.post("/centinela/test-call", input).then((res) => res.data?.data),
  });
}

export function useUpdateCentinelaUserNotifications() {
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const isDemo = useCompanyDemoFlag("centinela_demo");

  return useMutation({
    mutationKey: CENTINELA_USER_NOTIFICATIONS_MUTATION,
    mutationFn: ({
      userId,
      input,
    }: {
      userId: string;
      input: CentinelaUserNotificationsInput;
    }): Promise<unknown> =>
      api
        .put(`/centinela/users/${userId}/notifications`, input, {
          params: demoParams(isDemo),
        })
        .then((res) => res.data?.data),
    onMutate: async ({ userId, input }) => {
      const queryKey = qk.centinela.users(companyId, isDemo);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<CentinelaUsersResponse>(queryKey);

      queryClient.setQueryData<CentinelaUsersResponse>(queryKey, (current) =>
        current
          ? {
              ...current,
              users: current.users.map((user) =>
                user.user_id === userId ? { ...user, notificaciones: input } : user,
              ),
            }
          : current,
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (!context?.previous) return;
      queryClient.setQueryData(qk.centinela.users(companyId, isDemo), context.previous);
    },
    onSettled: () => {
      const pending = queryClient.isMutating({
        mutationKey: CENTINELA_USER_NOTIFICATIONS_MUTATION,
      });
      if (pending > 1) return;

      queryClient.invalidateQueries({
        queryKey: qk.centinela.users(companyId, isDemo),
      });
    },
  });
}

function useCustodyInvalidator() {
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const isDemo = useCompanyDemoFlag("centinela_demo");

  return (deviceId: string) => {
    queryClient.invalidateQueries({
      queryKey: qk.centinela.unitCustody(companyId, deviceId),
    });
    queryClient.invalidateQueries({
      queryKey: qk.centinela.units(companyId, isDemo),
    });
    queryClient.invalidateQueries({
      queryKey: qk.centinela.unit(companyId, deviceId, isDemo),
    });
  };
}

export function useUpdateCentinelaUnitCustody() {
  const invalidate = useCustodyInvalidator();
  const isDemo = useCompanyDemoFlag("centinela_demo");

  return useMutation({
    mutationFn: ({
      deviceId,
      input,
    }: {
      deviceId: string;
      input: CentinelaUnitCustodyInput;
    }): Promise<unknown> =>
      api
        .put(`/centinela/units/${deviceId}/custody`, input, {
          params: demoParams(isDemo),
        })
        .then((res) => res.data?.data),
    onSuccess: (_data, { deviceId }) => invalidate(deviceId),
  });
}

export function useEndCentinelaUnitCustody() {
  const invalidate = useCustodyInvalidator();
  const isDemo = useCompanyDemoFlag("centinela_demo");

  return useMutation({
    mutationFn: (deviceId: string): Promise<unknown> =>
      api
        .delete(`/centinela/units/${deviceId}/custody`, {
          params: demoParams(isDemo),
        })
        .then((res) => res.data?.data),
    onSuccess: (_data, deviceId) => invalidate(deviceId),
  });
}

export function useEndCentinelaTripCustody() {
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const isDemo = useCompanyDemoFlag("centinela_demo");

  return useMutation({
    mutationFn: (tripId: string): Promise<unknown> =>
      api
        .delete(`/centinela/trips/${tripId}/custody`, { params: demoParams(isDemo) })
        .then((res) => res.data?.data),
    onSuccess: (_data, tripId) => {
      queryClient.invalidateQueries({
        queryKey: qk.centinela.tripCustody(companyId, tripId),
      });
      queryClient.invalidateQueries({
        queryKey: qk.centinela.units(companyId, isDemo),
      });
    },
  });
}

export function usePatchCentinelaAlert() {
  const isDemo = useCompanyDemoFlag("centinela_demo");

  return useMutation({
    mutationFn: ({
      alertTypeId,
      input,
    }: {
      alertTypeId: string;
      input: CentinelaAlertPatchInput;
    }): Promise<CentinelaAlertPatchResponse> =>
      api
        .patch(`/centinela/config/alerts/${alertTypeId}`, input, {
          params: demoParams(isDemo),
        })
        .then((res) => res.data?.data),
  });
}

export function useUpdateCentinelaNumero() {
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const isDemo = useCompanyDemoFlag("centinela_demo");

  return useMutation({
    mutationFn: (input: CentinelaNumeroInput): Promise<unknown> =>
      api
        .put("/centinela/numeros", input, { params: demoParams(isDemo) })
        .then((res) => res.data?.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.centinela.numeros(companyId, isDemo),
      });
    },
  });
}

export function useUpdateCentinelaVoz() {
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const isDemo = useCompanyDemoFlag("centinela_demo");

  return useMutation({
    mutationFn: (input: CentinelaVozInput): Promise<CentinelaVozResponse> =>
      api.put("/centinela/voces", input).then((res) => res.data?.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.centinela.voces(companyId, isDemo),
      });
    },
  });
}

export function useCallCentinelaUnit() {
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const isDemo = useCompanyDemoFlag("centinela_demo");

  return useMutation({
    mutationFn: ({
      deviceId,
      input,
    }: {
      deviceId: string;
      input: CentinelaUnitCallInput;
    }): Promise<CentinelaUnitCallResponse> =>
      api
        .post(`/centinela/units/${deviceId}/call`, input)
        .then((res) => res.data?.data),
    onSuccess: (_data, { deviceId }) => {
      queryClient.invalidateQueries({
        queryKey: qk.centinela.unitCalls(companyId, deviceId, isDemo),
      });
      queryClient.invalidateQueries({
        queryKey: qk.centinela.unit(companyId, deviceId, isDemo),
      });
      queryClient.invalidateQueries({
        queryKey: qk.centinela.units(companyId, isDemo),
      });
    },
  });
}

export function useCentinelaTestNotification() {
  return useMutation({
    mutationFn: (
      input: CentinelaTestNotificationInput,
    ): Promise<CentinelaTestNotificationResponse> =>
      api.post("/centinela/test-notification", input).then((res) => res.data?.data),
  });
}
